import { useState, useEffect } from 'react';
import { eventTryoutAPI, tryoutAdminAPI, usersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Calendar, Clock, Users, FileText, UserPlus, Play, Square, RotateCcw, DollarSign, Gift } from 'lucide-react';
import { toast } from 'sonner';
import EventParticipantsDialog from './EventParticipantsDialog';

export default function EventTryOutTab() {
  const [events, setEvents] = useState([]);
  const [tryouts, setTryouts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [participantsDialog, setParticipantsDialog] = useState({ open: false, event: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [eventsRes, tryoutsRes, usersRes] = await Promise.all([
        eventTryoutAPI.getAll(),
        tryoutAdminAPI.getAll(),
        usersAPI.getAll()
      ]);
      setEvents(eventsRes.data || []);
      setTryouts(tryoutsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      if (dialog.mode === 'create') {
        await eventTryoutAPI.create(formData);
        toast.success('Event Try Out berhasil dibuat');
      } else {
        await eventTryoutAPI.update(dialog.data.id, formData);
        toast.success('Event Try Out berhasil diperbarui');
      }
      setDialog({ open: false, mode: 'create', data: null });
      loadData();
    } catch (error) {
      toast.error('Gagal menyimpan: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async () => {
    try {
      await eventTryoutAPI.delete(deleteDialog.id);
      toast.success('Event Try Out berhasil dihapus');
      setDeleteDialog({ open: false, id: null });
      loadData();
    } catch (error) {
      toast.error('Gagal menghapus');
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      await eventTryoutAPI.toggleStatus(id);
      toast.success(currentStatus ? 'Event dinonaktifkan' : 'Event diaktifkan');
      loadData();
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

  const handleStart = async (id) => {
    try {
      await eventTryoutAPI.start(id);
      toast.success('Event Try Out berhasil dimulai!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memulai event');
    }
  };

  const handleStop = async (id) => {
    try {
      await eventTryoutAPI.stop(id);
      toast.success('Event Try Out berhasil dihentikan');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghentikan event');
    }
  };

  const handleReset = async (id) => {
    try {
      await eventTryoutAPI.reset(id);
      toast.success('Event Try Out berhasil direset');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mereset event');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-700';
      case 'ongoing': return 'bg-green-100 text-green-700';
      case 'ended': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'scheduled': return 'Terjadwal';
      case 'ongoing': return 'Berlangsung';
      case 'ended': return 'Selesai';
      default: return status;
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#4472C4]/30 border-t-[#4472C4] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Event Try Out</h2>
          <p className="text-slate-500">Kelola Try Out khusus untuk user tertentu dengan jadwal</p>
        </div>
        <Button
          onClick={() => setDialog({ open: true, mode: 'create', data: null })}
          className="bg-[#4472C4] hover:bg-[#3861a8]"
          data-testid="create-event-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Buat Event
        </Button>
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Belum ada Event Try Out</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setDialog({ open: true, mode: 'create', data: null })}
            >
              Buat Event Pertama
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {events.map((event) => (
            <Card key={event.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-slate-900">{event.title}</h3>
                      <Badge className={getStatusColor(event.status)}>
                        {getStatusLabel(event.status)}
                      </Badge>
                      <Badge 
                        className={`cursor-pointer ${event.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                        onClick={() => handleToggle(event.id, event.is_active)}
                        data-testid={`toggle-event-${event.id}`}
                      >
                        {event.is_active ? 'Aktif' : 'Nonaktif'}
                      </Badge>
                      {/* Free/Paid Badge */}
                      <Badge className={event.is_free ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {event.is_free ? <><Gift className="w-3 h-3 mr-1" />Gratis</> : <><DollarSign className="w-3 h-3 mr-1" />{formatPrice(event.price)}</>}
                      </Badge>
                      {event.allow_self_registration && (
                        <Badge className="bg-purple-100 text-purple-700">
                          Registrasi Mandiri
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-sm text-slate-500 mb-3">{event.description || '-'}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <FileText className="w-4 h-4" />
                        <span className="truncate">{event.tryout_title || 'Try Out'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Users className="w-4 h-4" />
                        <span>{event.participant_count || 0} Peserta</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDateTime(event.start_time)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="w-4 h-4" />
                        <span>s/d {formatDateTime(event.end_time)}</span>
                      </div>
                      {event.max_participants && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Users className="w-4 h-4" />
                          <span>Kuota: {event.participant_count || 0}/{event.max_participants}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    {/* Start/Stop/Reset Controls */}
                    <div className="flex items-center gap-2">
                      {event.status === 'scheduled' && !event.is_started && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleStart(event.id)}
                          data-testid={`start-event-${event.id}`}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Mulai
                        </Button>
                      )}
                      {(event.status === 'ongoing' || event.is_started) && !event.is_stopped && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleStop(event.id)}
                          data-testid={`stop-event-${event.id}`}
                        >
                          <Square className="w-4 h-4 mr-1" />
                          Stop
                        </Button>
                      )}
                      {event.is_stopped && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReset(event.id)}
                          data-testid={`reset-event-${event.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Reset
                        </Button>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[#4472C4] hover:text-[#3861a8] hover:bg-blue-50"
                        onClick={() => setParticipantsDialog({ open: true, event })}
                        data-testid={`manage-participants-${event.id}`}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Peserta
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDialog({ open: true, mode: 'edit', data: event })}
                        data-testid={`edit-event-${event.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteDialog({ open: true, id: event.id })}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <EventDialog
        open={dialog.open}
        mode={dialog.mode}
        data={dialog.data}
        tryouts={tryouts}
        users={users.filter(u => u.role === 'student')}
        onClose={() => setDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSave}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, id: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Event Try Out?</AlertDialogTitle>
            <AlertDialogDescription>
              Aksi ini tidak dapat dibatalkan. Event Try Out akan dihapus secara permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Participants Dialog */}
      <EventParticipantsDialog
        open={participantsDialog.open}
        event={participantsDialog.event}
        onClose={() => setParticipantsDialog({ open: false, event: null })}
      />
    </div>
  );
}

function EventDialog({ open, mode, data, tryouts, users, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tryout_id: '',
    target_type: 'individual',
    user_ids: [],
    start_time: '',
    end_time: '',
    duration_override: '',
    max_attempts: 1,
    is_active: true,
    // New fields
    is_free: true,
    price: 0,
    allow_self_registration: false,
    max_participants: '',
    registration_deadline: ''
  });

  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (!open) return; // Only run when dialog opens
    
    if (data) {
      setFormData({
        title: data.title || '',
        description: data.description || '',
        tryout_id: data.tryout_id || '',
        target_type: data.target_type || 'individual',
        user_ids: data.user_ids || [],
        start_time: data.start_time ? data.start_time.slice(0, 16) : '',
        end_time: data.end_time ? data.end_time.slice(0, 16) : '',
        duration_override: data.duration_override || '',
        max_attempts: data.max_attempts || 1,
        is_active: data.is_active !== false,
        is_free: data.is_free !== false,
        price: data.price || 0,
        allow_self_registration: data.allow_self_registration || false,
        max_participants: data.max_participants || '',
        registration_deadline: data.registration_deadline ? data.registration_deadline.slice(0, 16) : ''
      });
    } else {
      // Use first tryout ID only when creating new event
      const defaultTryoutId = tryouts.length > 0 ? tryouts[0].id : '';
      setFormData({
        title: '',
        description: '',
        tryout_id: defaultTryoutId,
        target_type: 'individual',
        user_ids: [],
        start_time: '',
        end_time: '',
        duration_override: '',
        max_attempts: 1,
        is_active: true,
        is_free: true,
        price: 0,
        allow_self_registration: false,
        max_participants: '',
        registration_deadline: ''
      });
    }
    setUserSearch('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data?.id]); // Only depend on open and data.id for stability

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.tryout_id) {
      toast.error('Pilih Try Out dasar');
      return;
    }
    // Allow no user_ids if self-registration is enabled
    if (formData.user_ids.length === 0 && !formData.allow_self_registration) {
      toast.error('Pilih minimal satu peserta atau aktifkan Registrasi Mandiri');
      return;
    }
    if (!formData.start_time || !formData.end_time) {
      toast.error('Atur waktu mulai dan selesai');
      return;
    }

    const submitData = {
      ...formData,
      start_time: new Date(formData.start_time).toISOString(),
      end_time: new Date(formData.end_time).toISOString(),
      registration_deadline: formData.registration_deadline ? new Date(formData.registration_deadline).toISOString() : null,
      max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
      price: formData.is_free ? 0 : (parseFloat(formData.price) || 0),
      duration_override: formData.duration_override ? parseInt(formData.duration_override) : null
    };

    onSave(submitData);
  };

  const toggleUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter(id => id !== userId)
        : [...prev.user_ids, userId]
    }));
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Buat Event Try Out' : 'Edit Event Try Out'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Judul Event</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Event Try Out CPNS Batch 1"
              required
              data-testid="event-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Deskripsi</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Deskripsi event..."
            />
          </div>

          <div className="space-y-2">
            <Label>Try Out Dasar</Label>
            <Select 
              value={formData.tryout_id} 
              onValueChange={(v) => setFormData({ ...formData, tryout_id: v })}
            >
              <SelectTrigger data-testid="event-tryout-select">
                <SelectValue placeholder="Pilih Try Out" />
              </SelectTrigger>
              <SelectContent>
                {tryouts.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.title} ({t.question_count} soal)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Waktu Mulai</Label>
              <Input
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                required
                data-testid="event-start-time"
              />
            </div>
            <div className="space-y-2">
              <Label>Waktu Selesai</Label>
              <Input
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                required
                data-testid="event-end-time"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Override Durasi (menit)</Label>
              <Input
                type="number"
                value={formData.duration_override}
                onChange={(e) => setFormData({ ...formData, duration_override: e.target.value })}
                placeholder="Kosongkan untuk default"
                min={1}
              />
            </div>
            <div className="space-y-2">
              <Label>Max Percobaan</Label>
              <Input
                type="number"
                value={formData.max_attempts}
                onChange={(e) => setFormData({ ...formData, max_attempts: parseInt(e.target.value) || 1 })}
                min={1}
              />
            </div>
          </div>

          {/* NEW: Pricing & Registration Settings */}
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-slate-900 mb-3">Pengaturan Harga & Pendaftaran</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Event Gratis</Label>
                  <p className="text-xs text-slate-500">Peserta tidak perlu bayar</p>
                </div>
                <Switch
                  checked={formData.is_free}
                  onCheckedChange={(v) => setFormData({ ...formData, is_free: v, price: v ? 0 : formData.price })}
                />
              </div>
              
              {!formData.is_free && (
                <div className="space-y-2">
                  <Label>Harga Event (Rp)</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="50000"
                    min={0}
                    data-testid="event-price-input"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Registrasi Mandiri</Label>
                  <p className="text-xs text-slate-500">Siswa bisa daftar sendiri</p>
                </div>
                <Switch
                  checked={formData.allow_self_registration}
                  onCheckedChange={(v) => setFormData({ ...formData, allow_self_registration: v })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Max Peserta (opsional)</Label>
                <Input
                  type="number"
                  value={formData.max_participants}
                  onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                  placeholder="Tidak terbatas"
                  min={1}
                />
              </div>
            </div>

            {formData.allow_self_registration && (
              <div className="space-y-2 mt-4">
                <Label>Batas Waktu Pendaftaran (opsional)</Label>
                <Input
                  type="datetime-local"
                  value={formData.registration_deadline}
                  onChange={(e) => setFormData({ ...formData, registration_deadline: e.target.value })}
                />
                <p className="text-xs text-slate-500">Kosongkan jika pendaftaran dibuka sampai event berakhir</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Target Peserta</Label>
            <Select 
              value={formData.target_type} 
              onValueChange={(v) => setFormData({ ...formData, target_type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual (User Tertentu)</SelectItem>
                <SelectItem value="group">Kelompok (Multiple Users)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Pilih Peserta ({formData.user_ids.length} dipilih)</Label>
            <Input
              placeholder="Cari nama atau email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <div className="border rounded-lg max-h-48 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <p className="text-center text-slate-500 py-4">Tidak ada user ditemukan</p>
              ) : (
                filteredUsers.map(user => {
                  const isSelected = formData.user_ids.includes(user.id);
                  return (
                    <div 
                      key={user.id}
                      className={`flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b last:border-0 ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => toggleUser(user.id)}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-[#4472C4] border-[#4472C4]' : 'border-slate-300'
                      }`}>
                        {isSelected && <span className="text-white text-xs">✓</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{user.name}</p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
            />
            <span>Aktif</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" className="bg-[#4472C4] hover:bg-[#3861a8]" data-testid="save-event-btn">
              {mode === 'create' ? 'Buat Event' : 'Simpan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
